import { makeAutoObservable } from "mobx"
import type { HarnessId } from "./providerEvents/harnessEventTypes"
import type {
    ProviderCapabilities,
    ProviderListResult,
    ProviderModel,
    ProviderModelCapabilities,
    ProviderModelLimits,
    ProviderModelPricing,
} from "./nest/generated"
import { sortModelsForPresentation } from "./modelPresentation"

export interface HarnessMetaEntry {
    name: string
    vendor: string
    providerId: "claude" | "codex" | "grok" | "opencode"
}

export interface ModelEntry {
    id: string
    selector?: string
    class?: string
    latest?: boolean
    fullId?: string
    label: string
    displayClass: string
    tier?: string
    legacy?: boolean
    default?: boolean
    nativeProvider?: string
    status?: string
    releaseDate?: string
    capabilities?: ProviderModelCapabilities
    pricing?: ProviderModelPricing
    limits?: ProviderModelLimits
    variants?: string[]
}

export interface HarnessModelConfig {
    models: ModelEntry[]
    defaultModel: string
    installed: boolean
    available: boolean
    capabilities?: ProviderCapabilities
}

export const HARNESS_META: Record<HarnessId, HarnessMetaEntry> = {
    "claude-code": {
        name: "Claude Code",
        vendor: "Anthropic",
        providerId: "claude",
    },
    codex: { name: "Codex", vendor: "OpenAI", providerId: "codex" },
    "grok-build": { name: "Grok Build", vendor: "xAI", providerId: "grok" },
    opencode: { name: "OpenCode", vendor: "OpenCode", providerId: "opencode" },
}

export const DEFAULT_HARNESS_ID: HarnessId = "claude-code"
export const DEFAULT_MODEL = "default"

export const HARNESS_IDS: HarnessId[] = ["claude-code", "codex", "grok-build", "opencode"]

function emptyConfigs(): Record<HarnessId, HarnessModelConfig> {
    return {
        "claude-code": {
            models: [],
            defaultModel: DEFAULT_MODEL,
            installed: false,
            available: false,
        },
        codex: {
            models: [],
            defaultModel: DEFAULT_MODEL,
            installed: false,
            available: false,
        },
        "grok-build": {
            models: [],
            defaultModel: DEFAULT_MODEL,
            installed: false,
            available: false,
        },
        opencode: {
            models: [],
            defaultModel: DEFAULT_MODEL,
            installed: false,
            available: false,
        },
    }
}

export function harnessIdForProvider(providerId: string | undefined): HarnessId | null {
    if (providerId === "claude") return "claude-code"
    if (providerId === "codex") return "codex"
    if (providerId === "grok") return "grok-build"
    if (providerId === "opencode") return "opencode"
    return null
}

export function isHarnessId(value: unknown): value is HarnessId {
    return value === "claude-code" || value === "codex" || value === "grok-build" || value === "opencode"
}

export function providerIdForHarness(harnessId: HarnessId): HarnessMetaEntry["providerId"] {
    return HARNESS_META[harnessId].providerId
}

function providerModelToEntry(model: ProviderModel): ModelEntry {
    return {
        id: model.modelId,
        ...(model.selector ? { selector: model.selector } : {}),
        ...(model.class ? { class: model.class } : {}),
        ...(model.latest ? { latest: true } : {}),
        ...(model.fullId ? { fullId: model.fullId } : {}),
        label: model.label ?? model.modelId,
        displayClass: model.displayClass ?? model.class ?? "Other",
        ...(model.tier ? { tier: model.tier } : {}),
        ...(model.legacy ? { legacy: true } : {}),
        ...(model.default ? { default: true } : {}),
        ...(model.nativeProvider ? { nativeProvider: model.nativeProvider } : {}),
        ...(model.status ? { status: model.status } : {}),
        ...(model.releaseDate ? { releaseDate: model.releaseDate } : {}),
        ...(model.capabilities ? { capabilities: model.capabilities } : {}),
        ...(model.pricing ? { pricing: model.pricing } : {}),
        ...(model.limits ? { limits: model.limits } : {}),
        ...(model.variants ? { variants: model.variants } : {}),
    }
}

function defaultModelForEntries(entries: ModelEntry[]): string {
    return entries.find((model) => model.default)?.id ?? entries[0]?.id ?? DEFAULT_MODEL
}

function findExactModelEntry(value: string, config: HarnessModelConfig): ModelEntry | undefined {
    return config.models.find((model) => model.id === value || model.fullId === value)
}

function getModelCompatibilityScore(value: string, model: ModelEntry): number {
    const lower = value.toLowerCase()
    let score = 0
    const candidates = [model.id, model.fullId, model.selector]
    if (model.latest) candidates.push(model.displayClass, model.class)
    for (const candidate of candidates) {
        const normalized = candidate?.toLowerCase()
        if (normalized && lower.includes(normalized)) {
            score = Math.max(score, normalized.length)
        }
    }
    return score
}

function findCompatibleModelEntry(value: string, config: HarnessModelConfig): ModelEntry | undefined {
    const exact = findExactModelEntry(value, config)
    if (exact) return exact

    let bestMatch: ModelEntry | undefined
    let bestScore = 0
    for (const model of config.models) {
        const score = getModelCompatibilityScore(value, model)
        if (score > bestScore) {
            bestMatch = model
            bestScore = score
            continue
        }
        if (score > 0 && score === bestScore && bestMatch && model.latest && !bestMatch.latest) {
            bestMatch = model
        }
    }
    return bestMatch
}

export class ProviderModelRegistry {
    configs: Record<HarnessId, HarnessModelConfig> = emptyConfigs()

    constructor() {
        makeAutoObservable(this)
    }

    replaceFromProviderList(result: ProviderListResult): void {
        const next = emptyConfigs()
        for (const provider of result.providers) {
            const harnessId = harnessIdForProvider(provider.providerId)
            if (!harnessId) continue
            const models = (provider.models ?? []).map(providerModelToEntry)
            next[harnessId] = {
                models,
                defaultModel: defaultModelForEntries(models),
                installed: provider.installed,
                available: provider.available,
                capabilities: provider.capabilities,
            }
        }
        this.configs = next
    }

    get harnessIds(): HarnessId[] {
        return HARNESS_IDS
    }

    get selectableHarnessIds(): HarnessId[] {
        const available = HARNESS_IDS.filter((harnessId) => {
            const config = this.configs[harnessId]
            return config.installed && config.available
        })
        return available.length > 0 ? available : [DEFAULT_HARNESS_ID]
    }

    modelsForHarness(harnessId: HarnessId): ModelEntry[] {
        return this.configs[harnessId]?.models ?? []
    }

    getDefaultModelForHarness(harnessId: HarnessId): string {
        return this.configs[harnessId]?.defaultModel ?? DEFAULT_MODEL
    }

    isModelForHarness(harnessId: HarnessId, modelId: string | undefined): modelId is string {
        if (!modelId) return false
        if (modelId === DEFAULT_MODEL) return true
        const models = this.modelsForHarness(harnessId)
        if (models.length === 0) return true
        return models.some((model) => model.id === modelId || model.fullId === modelId)
    }

    resolveModelForHarness(value: string, harnessId: HarnessId): string {
        if (!value || value === DEFAULT_MODEL) return this.getDefaultModelForHarness(harnessId)
        const config = this.configs[harnessId]
        if (!config || config.models.length === 0) return value
        return findCompatibleModelEntry(value, config)?.id ?? value
    }

    getModelFullId(value: string, harnessId?: HarnessId): string {
        if (harnessId) {
            const found = this.modelsForHarness(harnessId).find((model) => model.id === value)
            if (found?.fullId) return found.fullId
        }
        for (const id of HARNESS_IDS) {
            const found = this.modelsForHarness(id).find((model) => model.id === value)
            if (found?.fullId) return found.fullId
        }
        return value
    }

    getVisibleModelEntries(harnessId: HarnessId): ModelEntry[] {
        return sortModelsForPresentation(this.modelsForHarness(harnessId))
    }

    getVisibleModelId(value: string, harnessId: HarnessId): string {
        return this.resolveModelForHarness(value, harnessId)
    }

    capabilitiesForHarness(harnessId: HarnessId): ProviderCapabilities | undefined {
        return this.configs[harnessId]?.capabilities
    }

    supportsSteering(harnessId: HarnessId): boolean {
        return this.capabilitiesForHarness(harnessId)?.steering === true
    }

    normalizeModelClass(modelId: string): string {
        for (const id of HARNESS_IDS) {
            const found = this.modelsForHarness(id).find((model) => model.id === modelId || model.fullId === modelId)
            if (found) return found.displayClass
        }
        return normalizeModelClass(modelId)
    }
}

export const providerModelRegistry = new ProviderModelRegistry()

export function getModelsForHarness(harnessId: HarnessId): ModelEntry[] {
    return providerModelRegistry.modelsForHarness(harnessId)
}

export function getDefaultModelForHarness(harnessId: HarnessId): string {
    return providerModelRegistry.getDefaultModelForHarness(harnessId)
}

export function supportsSteeringForHarness(harnessId: HarnessId): boolean {
    return providerModelRegistry.supportsSteering(harnessId)
}

export function resolveModelForHarness(value: string, harnessId: HarnessId): string {
    return providerModelRegistry.resolveModelForHarness(value, harnessId)
}

export function getModelFullId(value: string, harnessId?: HarnessId): string {
    return providerModelRegistry.getModelFullId(value, harnessId)
}

export function normalizeModelClass(modelId: string): string {
    const lower = modelId.toLowerCase()
    if (lower.includes("mythos")) return "Mythos"
    if (lower.includes("fable")) return "Fable"
    if (lower.includes("opus")) return "Opus"
    if (lower.includes("sonnet")) return "Sonnet"
    if (lower.includes("haiku")) return "Haiku"
    if (lower.includes("codex")) return "Codex"
    if (lower.includes("grok")) return "Grok"
    if (lower.startsWith("gpt-")) return "Codex"
    if (lower === DEFAULT_MODEL) return "Default"
    return "Other"
}
